import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../api/client';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Load user from localStorage on mount
  useEffect(() => {
    const loadUser = async () => {
      const token = localStorage.getItem('auth_token');
      const savedUser = localStorage.getItem('user');
      const savedCompany = localStorage.getItem('company');

      if (token && savedUser && savedCompany) {
        try {
          setUser(JSON.parse(savedUser));
          setCompany(JSON.parse(savedCompany));
          setIsAuthenticated(true);
          
          // Optionally verify token is still valid
          const data = await authAPI.getCurrentUser();
          setUser(data.user);
          setCompany(data.company);
        } catch (error) {
          console.error('Error loading user:', error);
          logout();
        }
      }
      
      setLoading(false);
    };

    loadUser();
  }, []);

  const login = async (email, password) => {
    try {
      const data = await authAPI.login(email, password);
      
      // Save to localStorage
      localStorage.setItem('auth_token', data.access_token);
      localStorage.setItem('user', JSON.stringify(data.user));
      localStorage.setItem('company', JSON.stringify(data.company));
      
      setUser(data.user);
      setCompany(data.company);
      setIsAuthenticated(true);
      
      return { success: true, data };
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Login failed'
      };
    }
  };

  const register = async (userData) => {
    try {
      const data = await authAPI.register(userData);
      
      // Save to localStorage
      localStorage.setItem('auth_token', data.access_token);
      localStorage.setItem('user', JSON.stringify(data.user));
      localStorage.setItem('company', JSON.stringify(data.company));
      
      setUser(data.user);
      setCompany(data.company);
      setIsAuthenticated(true);
      
      return { success: true, data };
    } catch (error) {
      console.error('Register error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Registration failed'
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    localStorage.removeItem('company');
    setUser(null);
    setCompany(null);
    setIsAuthenticated(false);
  };

  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const updateCompany = (companyData) => {
    setCompany(companyData);
    localStorage.setItem('company', JSON.stringify(companyData));
  };

  const refreshAuth = async () => {
    try {
      const data = await authAPI.getCurrentUser();
      setUser(data.user);
      setCompany(data.company);
      localStorage.setItem('user', JSON.stringify(data.user));
      localStorage.setItem('company', JSON.stringify(data.company));
      return data;
    } catch (e) {
      console.error('refreshAuth failed', e);
    }
  };

  const value = {
    user,
    company,
    loading,
    isAuthenticated,
    login,
    register,
    logout,
    updateUser,
    updateCompany,
    refreshAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
